import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../hooks/useScrollReveal';

/* ─── Mockup: QR Scan Screen ─── */
function MockupQRScan() {
    return (
        <div className="relative w-full max-w-[260px] mx-auto">
            {/* Phone outline */}
            <div className="bg-slate-900 border-2 border-white/10 rounded-[2rem] p-3 shadow-2xl shadow-emerald-500/10">
                {/* Notch */}
                <div className="w-20 h-4 bg-slate-950 rounded-full mx-auto mb-3" />
                {/* Screen */}
                <div className="bg-slate-950 rounded-2xl p-6 flex flex-col items-center gap-4">
                    <div className="text-xs font-semibold text-white/50 tracking-widest uppercase">Scan QR Code</div>
                    {/* QR grid */}
                    <div className="grid grid-cols-7 gap-[3px] p-3 bg-white rounded-lg">
                        {Array.from({ length: 49 }).map((_, i) => {
                            const row = Math.floor(i / 7);
                            const col = i % 7;
                            const isCorner =
                                (row < 3 && col < 3) ||
                                (row < 3 && col > 3) ||
                                (row > 3 && col < 3);
                            const isCenter = row === 3 && col === 3;
                            const filled = isCorner || isCenter || [5, 12, 19, 30, 37, 44, 22, 26, 33, 40].includes(i);
                            return (
                                <div
                                    key={i}
                                    className={`w-3 h-3 rounded-[2px] ${filled ? 'bg-slate-900' : 'bg-white'}`}
                                />
                            );
                        })}
                    </div>
                    {/* Scanner line */}
                    <div className="w-full h-px bg-gradient-to-r from-transparent via-emerald-400 to-transparent animate-pulse" />
                    <div className="text-[10px] text-white/30 text-center">Link your WhatsApp account</div>
                </div>
            </div>
        </div>
    );
}

/* ─── Mockup: Add Contact Bar ─── */
function MockupAddContact() {
    return (
        <div className="w-full max-w-md mx-auto">
            <div className="bg-slate-900/80 border border-white/10 rounded-2xl p-6 shadow-2xl shadow-cyan-500/5">
                <div className="text-xs font-semibold text-white/40 tracking-widest uppercase mb-4">Add Contact</div>
                <div className="flex gap-2">
                    <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                        <span className="text-white/30 text-sm">+</span>
                        <span className="text-white/25 text-sm">Enter phone number...</span>
                    </div>
                    <div className="px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-sm font-semibold flex items-center gap-1.5 shrink-0">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        Track
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-[11px] text-white/20">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                    </svg>
                    International format required (e.g. +33612345678)
                </div>
            </div>
        </div>
    );
}

/* ─── Mockup: Tracking Card ─── */
function MockupTrackingCard() {
    const rttPoints = [340, 310, 380, 290, 320, 350, 310, 330, 340, 300, 280, 320];
    const maxRtt = Math.max(...rttPoints);
    const minRtt = Math.min(...rttPoints);
    const range = maxRtt - minRtt || 1;

    return (
        <div className="w-full max-w-md mx-auto">
            <div className="bg-slate-900/80 border border-white/10 rounded-2xl p-6 shadow-2xl shadow-emerald-500/5">
                {/* Contact header */}
                <div className="flex items-center gap-3 mb-5">
                    <div className="relative">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
                            JD
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-slate-900" />
                    </div>
                    <div className="flex-1">
                        <div className="text-white font-semibold text-sm">John Doe</div>
                        <div className="text-emerald-400 text-xs font-medium">Online (Phone)</div>
                    </div>
                    <div className="text-[10px] text-white/20 font-mono">2 devices</div>
                </div>

                {/* RTT chart */}
                <div className="bg-white/[0.03] rounded-xl p-4 mb-4">
                    <div className="text-[10px] text-white/30 font-semibold tracking-wider uppercase mb-3">RTT History</div>
                    <svg viewBox="0 0 240 60" className="w-full h-[60px]">
                        <defs>
                            <linearGradient id="rttGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#34d399" stopOpacity="0.3" />
                                <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
                            </linearGradient>
                        </defs>
                        {/* Area fill */}
                        <path
                            d={
                                `M0,${55 - ((rttPoints[0] - minRtt) / range) * 45} ` +
                                rttPoints.map((p, i) => `L${(i / (rttPoints.length - 1)) * 240},${55 - ((p - minRtt) / range) * 45}`).join(' ') +
                                ` L240,55 L0,55 Z`
                            }
                            fill="url(#rttGrad)"
                        />
                        {/* Line */}
                        <polyline
                            points={rttPoints.map((p, i) => `${(i / (rttPoints.length - 1)) * 240},${55 - ((p - minRtt) / range) * 45}`).join(' ')}
                            fill="none"
                            stroke="#34d399"
                            strokeWidth="2"
                            strokeLinejoin="round"
                        />
                        {/* Dots */}
                        {rttPoints.map((p, i) => (
                            <circle
                                key={i}
                                cx={(i / (rttPoints.length - 1)) * 240}
                                cy={55 - ((p - minRtt) / range) * 45}
                                r="2.5"
                                fill="#34d399"
                            />
                        ))}
                    </svg>
                </div>

                {/* Stats row */}
                <div className="flex gap-3">
                    <div className="flex-1 bg-white/[0.03] rounded-lg px-3 py-2">
                        <div className="text-[10px] text-white/30">Avg RTT</div>
                        <div className="text-white font-bold text-sm">340 ms</div>
                    </div>
                    <div className="flex-1 bg-white/[0.03] rounded-lg px-3 py-2">
                        <div className="text-[10px] text-white/30">Median</div>
                        <div className="text-white font-bold text-sm">310 ms</div>
                    </div>
                    <div className="flex-1 bg-white/[0.03] rounded-lg px-3 py-2">
                        <div className="text-[10px] text-white/30">Status</div>
                        <div className="text-emerald-400 font-bold text-sm">Online</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─── Mockup: Dashboard Overview ─── */
function MockupDashboard() {
    const contacts = [
        { initials: 'AS', name: 'Alice S.', status: 'Online', color: 'text-emerald-400', dot: 'bg-emerald-400', devices: 2 },
        { initials: 'MK', name: 'Marc K.', status: 'Standby', color: 'text-amber-400', dot: 'bg-amber-400', devices: 1 },
        { initials: 'LR', name: 'Lisa R.', status: 'Offline', color: 'text-red-400', dot: 'bg-red-400', devices: 3 },
    ];

    return (
        <div className="bg-slate-900/80 border border-white/10 rounded-2xl p-5 shadow-2xl">
            <div className="text-xs font-semibold text-white/40 tracking-widest uppercase mb-4">Dashboard</div>
            <div className="space-y-3">
                {contacts.map((c) => (
                    <div key={c.initials} className="flex items-center gap-3 bg-white/[0.03] rounded-xl px-4 py-3">
                        <div className="relative">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-600 flex items-center justify-center text-white text-xs font-bold">
                                {c.initials}
                            </div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${c.dot} border-2 border-slate-900`} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-white text-sm font-medium truncate">{c.name}</div>
                            <div className={`text-xs ${c.color}`}>{c.status}</div>
                        </div>
                        <div className="text-[10px] text-white/20">{c.devices}d</div>
                        {/* Mini sparkline */}
                        <svg viewBox="0 0 40 16" className="w-10 h-4">
                            <polyline
                                points={
                                    c.status === 'Online'
                                        ? '0,12 8,6 16,8 24,3 32,5 40,2'
                                        : c.status === 'Standby'
                                        ? '0,8 8,10 16,6 24,9 32,7 40,8'
                                        : '0,4 8,10 16,12 24,14 32,13 40,14'
                                }
                                fill="none"
                                stroke={c.status === 'Online' ? '#34d399' : c.status === 'Standby' ? '#fbbf24' : '#f87171'}
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─── Mockup: Contact Detail ─── */
function MockupContactDetail() {
    const ringData = [
        { label: 'Online', pct: 72, color: '#34d399', radius: 40 },
        { label: 'Standby', pct: 18, color: '#fbbf24', radius: 30 },
        { label: 'Offline', pct: 10, color: '#f87171', radius: 20 },
    ];

    return (
        <div className="bg-slate-900/80 border border-white/10 rounded-2xl p-5 shadow-2xl">
            <div className="text-xs font-semibold text-white/40 tracking-widest uppercase mb-4">Contact Detail</div>

            {/* Progress rings */}
            <div className="flex justify-center gap-6 mb-5">
                {ringData.map((r) => {
                    const circumference = 2 * Math.PI * r.radius;
                    const dashOffset = circumference - (r.pct / 100) * circumference;
                    return (
                        <div key={r.label} className="flex flex-col items-center gap-1">
                            <svg width={r.radius * 2 + 10} height={r.radius * 2 + 10} className="transform -rotate-90">
                                <circle
                                    cx={r.radius + 5}
                                    cy={r.radius + 5}
                                    r={r.radius}
                                    fill="none"
                                    stroke="rgba(255,255,255,0.05)"
                                    strokeWidth="4"
                                />
                                <circle
                                    cx={r.radius + 5}
                                    cy={r.radius + 5}
                                    r={r.radius}
                                    fill="none"
                                    stroke={r.color}
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={dashOffset}
                                />
                            </svg>
                            <span className="text-xs font-bold text-white">{r.pct}%</span>
                            <span className="text-[10px] text-white/30">{r.label}</span>
                        </div>
                    );
                })}
            </div>

            {/* Mini heatmap */}
            <div className="bg-white/[0.03] rounded-xl p-3">
                <div className="text-[10px] text-white/30 mb-2 font-semibold tracking-wider uppercase">Weekly Heatmap</div>
                <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: 28 }).map((_, i) => {
                        const intensities = [0.05, 0.15, 0.3, 0.5, 0.7];
                        const intensity = intensities[Math.floor(Math.random() * intensities.length)];
                        return (
                            <div
                                key={i}
                                className="aspect-square rounded-[3px]"
                                style={{ backgroundColor: `rgba(52, 211, 153, ${intensity})` }}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/* ─── Mockup: Smart Insights ─── */
function MockupInsights() {
    const insights = [
        { icon: 'M12 8v4l3 3', label: 'Peak activity: 14:00 - 16:00' },
        { icon: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01M3.757 12.243a9.953 9.953 0 0116.486 0', label: 'Likely on WiFi (home)' },
        { icon: 'M20.354 15.354A9 9 0 018.646 3.646', label: 'Sleep window: 23:00 - 07:00' },
        { icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6m14 0v-6a2 2 0 00-2-2h-2a2 2 0 00-2 2v6', label: 'Work hours: 08:30 - 17:30' },
    ];

    return (
        <div className="bg-slate-900/80 border border-white/10 rounded-2xl p-5 shadow-2xl">
            <div className="text-xs font-semibold text-white/40 tracking-widest uppercase mb-4">Smart Insights</div>
            <div className="space-y-3">
                {insights.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/[0.03] rounded-xl px-4 py-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center shrink-0">
                            <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                            </svg>
                        </div>
                        <span className="text-sm text-white/70">{item.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─── Feature Icon Components ─── */
function IconTiming() {
    return (
        <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );
}

function IconActivity() {
    return (
        <svg className="w-7 h-7 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13h2l3-8 4 16 3-8h6" />
        </svg>
    );
}

function IconMultiDevice() {
    return (
        <svg className="w-7 h-7 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    );
}

function IconStealth() {
    return (
        <svg className="w-7 h-7 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l18 18" />
        </svg>
    );
}

/* ─── Main Landing Page ─── */
export function LandingPage() {
    const { t } = useTranslation();
    const [heroRef, heroVisible] = useScrollReveal<HTMLDivElement>(0.1);
    const [stepsRef, stepsVisible] = useScrollReveal<HTMLDivElement>();
    const [step1Ref, step1Visible] = useScrollReveal<HTMLDivElement>();
    const [step2Ref, step2Visible] = useScrollReveal<HTMLDivElement>();
    const [step3Ref, step3Visible] = useScrollReveal<HTMLDivElement>();
    const [featRef, featVisible] = useScrollReveal<HTMLDivElement>();
    const [showcaseRef, showcaseVisible] = useScrollReveal<HTMLDivElement>();
    const [sc1Ref, sc1Visible] = useScrollReveal<HTMLDivElement>();
    const [sc2Ref, sc2Visible] = useScrollReveal<HTMLDivElement>();
    const [sc3Ref, sc3Visible] = useScrollReveal<HTMLDivElement>();
    const [ctaRef, ctaVisible] = useScrollReveal<HTMLDivElement>();

    const features = [
        { key: 'timing', Icon: IconTiming, gradient: 'from-emerald-500/20 to-emerald-500/5', hoverGlow: 'hover:shadow-emerald-500/10' },
        { key: 'activity', Icon: IconActivity, gradient: 'from-cyan-500/20 to-cyan-500/5', hoverGlow: 'hover:shadow-cyan-500/10' },
        { key: 'multi', Icon: IconMultiDevice, gradient: 'from-violet-500/20 to-violet-500/5', hoverGlow: 'hover:shadow-violet-500/10' },
        { key: 'stealth', Icon: IconStealth, gradient: 'from-rose-500/20 to-rose-500/5', hoverGlow: 'hover:shadow-rose-500/10' },
    ];

    return (
        <div className="bg-slate-950 text-white overflow-hidden">

            {/* ────────── 1. HERO ────────── */}
            <section className="relative min-h-screen flex items-center pt-20">
                {/* Animated background */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-emerald-500/8 rounded-full blur-[140px] animate-pulse" />
                    <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-cyan-500/8 rounded-full blur-[140px] animate-pulse" style={{ animationDelay: '1.5s' }} />
                    <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] bg-violet-500/5 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '3s' }} />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/[0.03] rounded-full" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/[0.04] rounded-full" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-white/[0.05] rounded-full" />
                </div>

                <div
                    ref={heroRef}
                    className={`relative max-w-7xl mx-auto px-6 py-20 text-center transition-all duration-1000 ${
                        heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                    }`}
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-sm">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-sm text-white/70">{t('landing.hero.badge')}</span>
                    </div>

                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tight max-w-5xl mx-auto mb-8">
                        <span className="bg-gradient-to-r from-white via-white to-white/60 bg-clip-text text-transparent">
                            {t('landing.hero.title')}
                        </span>
                    </h1>

                    <p className="text-lg md:text-xl text-white/50 max-w-2xl mx-auto mb-12 leading-relaxed">
                        {t('landing.hero.subtitle')}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/auth?mode=signup"
                            className="group px-8 py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold text-lg shadow-2xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all hover:-translate-y-0.5"
                        >
                            {t('landing.hero.cta')}
                            <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">&rarr;</span>
                        </Link>
                        <a
                            href="#steps"
                            className="px-8 py-4 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/5 hover:border-white/30 transition-all"
                        >
                            {t('landing.hero.ctaSecondary')}
                        </a>
                    </div>
                </div>
            </section>

            {/* ────────── 2. HOW IT WORKS — VISUAL STEPS ────────── */}
            <section id="steps" className="py-24 md:py-32 bg-white/[0.015] relative">
                {/* Vertical connecting gradient line (desktop) */}
                <div className="hidden lg:block absolute left-1/2 top-40 bottom-40 w-px bg-gradient-to-b from-emerald-500/30 via-cyan-500/20 to-emerald-500/30" />

                <div
                    ref={stepsRef}
                    className={`max-w-7xl mx-auto px-6 transition-all duration-1000 ${
                        stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                >
                    <div className="text-center mb-20">
                        <h2 className="text-4xl md:text-5xl font-black mb-4">{t('landing.steps.title')}</h2>
                        <p className="text-lg text-white/40 max-w-xl mx-auto">{t('landing.steps.subtitle')}</p>
                    </div>

                    {/* Step 1: Connect — text left, mockup right */}
                    <div
                        ref={step1Ref}
                        className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 md:mb-32 transition-all duration-1000 ${
                            step1Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                        }`}
                    >
                        <div className="order-2 lg:order-1">
                            <div className="text-7xl md:text-8xl font-black text-white/[0.04] leading-none mb-4">01</div>
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('landing.steps.s1.title')}</h3>
                            <p className="text-white/50 leading-relaxed text-lg">{t('landing.steps.s1.desc')}</p>
                        </div>
                        <div className="order-1 lg:order-2">
                            <MockupQRScan />
                        </div>
                    </div>

                    {/* Step 2: Add a number — mockup left, text right */}
                    <div
                        ref={step2Ref}
                        className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 md:mb-32 transition-all duration-1000 ${
                            step2Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                        }`}
                    >
                        <div>
                            <MockupAddContact />
                        </div>
                        <div>
                            <div className="text-7xl md:text-8xl font-black text-white/[0.04] leading-none mb-4">02</div>
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('landing.steps.s2.title')}</h3>
                            <p className="text-white/50 leading-relaxed text-lg">{t('landing.steps.s2.desc')}</p>
                        </div>
                    </div>

                    {/* Step 3: Real-time insights — text left, mockup right */}
                    <div
                        ref={step3Ref}
                        className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ${
                            step3Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                        }`}
                    >
                        <div className="order-2 lg:order-1">
                            <div className="text-7xl md:text-8xl font-black text-white/[0.04] leading-none mb-4">03</div>
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('landing.steps.s3.title')}</h3>
                            <p className="text-white/50 leading-relaxed text-lg">{t('landing.steps.s3.desc')}</p>
                        </div>
                        <div className="order-1 lg:order-2">
                            <MockupTrackingCard />
                        </div>
                    </div>
                </div>
            </section>

            {/* ────────── 3. FEATURES GRID ────────── */}
            <section id="features" className="py-24 md:py-32">
                <div
                    ref={featRef}
                    className={`max-w-7xl mx-auto px-6 transition-all duration-1000 ${
                        featVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                >
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-black mb-4">{t('landing.features.title')}</h2>
                        <p className="text-lg text-white/40 max-w-xl mx-auto">{t('landing.features.subtitle')}</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {features.map((f, idx) => (
                            <div
                                key={f.key}
                                className={`group relative bg-white/[0.03] border border-white/10 rounded-2xl p-8 transition-all duration-500 hover:bg-white/[0.05] hover:border-white/15 hover:shadow-2xl ${f.hoverGlow} ${
                                    featVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                                }`}
                                style={{ transitionDelay: `${idx * 120}ms` }}
                            >
                                {/* Gradient border glow on hover */}
                                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${f.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl`} />

                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-5`}>
                                    <f.Icon />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">{t(`landing.features.${f.key}.title`)}</h3>
                                <p className="text-white/40 leading-relaxed">{t(`landing.features.${f.key}.desc`)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ────────── 4. PRODUCT SHOWCASE ────────── */}
            <section className="py-24 md:py-32 bg-white/[0.015]">
                <div
                    ref={showcaseRef}
                    className={`max-w-7xl mx-auto px-6 transition-all duration-1000 ${
                        showcaseVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                >
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-black mb-4">{t('landing.showcase.title')}</h2>
                        <p className="text-lg text-white/40 max-w-xl mx-auto">{t('landing.showcase.subtitle')}</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* A) Dashboard Overview */}
                        <div
                            ref={sc1Ref}
                            className={`transition-all duration-700 ${
                                sc1Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                        >
                            <div className="text-sm font-semibold text-emerald-400 mb-3">{t('landing.showcase.dashboard.title')}</div>
                            <MockupDashboard />
                        </div>

                        {/* B) Contact Detail */}
                        <div
                            ref={sc2Ref}
                            className={`transition-all duration-700 ${
                                sc2Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                            style={{ transitionDelay: '150ms' }}
                        >
                            <div className="text-sm font-semibold text-cyan-400 mb-3">{t('landing.showcase.detail.title')}</div>
                            <MockupContactDetail />
                        </div>

                        {/* C) Smart Insights */}
                        <div
                            ref={sc3Ref}
                            className={`transition-all duration-700 ${
                                sc3Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                            style={{ transitionDelay: '300ms' }}
                        >
                            <div className="text-sm font-semibold text-violet-400 mb-3">{t('landing.showcase.insights.title')}</div>
                            <MockupInsights />
                        </div>
                    </div>
                </div>
            </section>

            {/* ────────── 5. FINAL CTA ────────── */}
            <section className="py-24 md:py-32">
                <div
                    ref={ctaRef}
                    className={`max-w-4xl mx-auto px-6 text-center transition-all duration-1000 ${
                        ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                    }`}
                >
                    <div className="relative bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-white/10 rounded-3xl p-12 md:p-20 overflow-hidden">
                        {/* Top edge glow */}
                        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
                        {/* Corner blobs */}
                        <div className="absolute -top-20 -right-20 w-60 h-60 bg-emerald-500/10 rounded-full blur-[80px]" />
                        <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-cyan-500/10 rounded-full blur-[80px]" />

                        <h2 className="text-4xl md:text-6xl font-black mb-6 relative">{t('landing.finalCta.title')}</h2>
                        <p className="text-lg text-white/50 mb-10 max-w-xl mx-auto relative">{t('landing.finalCta.subtitle')}</p>
                        <Link
                            to="/auth?mode=signup"
                            className="relative inline-block px-10 py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold text-lg shadow-2xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all hover:-translate-y-0.5"
                        >
                            {t('landing.finalCta.cta')}
                        </Link>
                        <p className="text-sm text-white/30 mt-6 relative">{t('landing.finalCta.note')}</p>
                    </div>
                </div>
            </section>
        </div>
    );
}
